import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import type {
  BlockExportMapping,
  ComponentBlockExport,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { isChildrenProp } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { flattenCssVariables } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/flattenCssVariables";
import type { ScreenSize } from "@circle-react-shared/ScreenSizeSelect";
import { mergeNewStyles } from "./mergeNewStyles";
import { mergeNewStylesWithCanvasSize } from "./mergeNewStylesWithCanvasSize";
import { isStylesProp } from "./typePredicates";

const updateBlockStyles = (
  block: BaseBlock,
  formValue: unknown,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
  canvasSize: ScreenSize,
  shouldProcessResponsiveOverrides: boolean = false,
): BaseBlock => {
  const parentProp = block.props.styles;

  if (!isStylesProp(parentProp)) return block;

  const flattenedStyles = flattenCssVariables(
    currentCanvasTheme.cssVariables,
    appearance,
  );
  const existingStyles = parentProp.value;

  let updatedStyles = {};

  if (!shouldProcessResponsiveOverrides) {
    updatedStyles = mergeNewStyles(existingStyles, formValue, flattenedStyles);
  } else {
    updatedStyles = mergeNewStylesWithCanvasSize(
      existingStyles,
      formValue,
      flattenedStyles,
      canvasSize,
    );
  }

  const newBlock = cloneDeep(block);

  set(newBlock, "props.styles.value", updatedStyles);

  return newBlock;
};

const updateBlockProp = (
  block: BaseBlock,
  key: string,
  value: unknown,
  blockMapping: BlockExportMapping,
): BaseBlock => {
  const propToBeUpdated = block.props[key];
  const isPropChildren = isChildrenProp(propToBeUpdated);

  if (isPropChildren) return block;

  if (!propToBeUpdated) {
    const blockFromMapping = blockMapping[block.type] as ComponentBlockExport;
    const defaultPropDataFromMapping = blockFromMapping.defaultData.props[key];

    if (!defaultPropDataFromMapping) {
      console.error(
        `Block property: ${key} doesn't exist on current map or on block mapping defaultData`,
      );
      return block;
    }

    const newBlock = cloneDeep(block);
    newBlock.props[key] = cloneDeep(defaultPropDataFromMapping);
    set(newBlock, `props.${key}.value`, value);
    return newBlock;
  }

  const newBlock = cloneDeep(block);
  set(newBlock, `props.${key}.value`, value);
  return newBlock;
};

export { updateBlockStyles, updateBlockProp };
